import { createApi } from '@reduxjs/toolkit/query/react'
import { handleError } from '../../commons/utils/utils'
import { setAdminUserInfo } from '../slices/adminUserSlice'
import { setPermissionsRefetched, setUserPermission } from '../slices/authSlice'
import { generateRESTQuery, getBaseQuery } from './apiUtils'
import apiConstants, { HTTP_METHODS } from './constants'

export const adminUsersApi = createApi({
  reducerPath: 'adminUsersApi',
  refetchOnFocus: false,
  baseQuery: getBaseQuery(),
  tagTypes: ['AdminUserDetails', 'AllAdminUsers'],
  endpoints: (builder) => ({
    getAllAdminUsers: builder.query({
      query: (requestParams: Omit<GetAdminUsersParam, 'TenantID'>) =>
        generateRESTQuery({
          path: apiConstants.ADMIN_USERS_REST_URL,
          queryParams: requestParams
        }),
      providesTags: [{ type: 'AllAdminUsers' }]
    }),
    createNewAdminUser: builder.mutation({
      query: (requestParams: AdminUserFormfields) =>
        generateRESTQuery({
          path: apiConstants.ADMIN_USERS_REST_URL,
          method: HTTP_METHODS.POST,
          body: requestParams
        }),
      invalidatesTags: [{ type: 'AllAdminUsers' }]
    }),
    validateAdminUserFormFields: builder.mutation({
      query: (requestParams: AdminUserFormfields) =>
        generateRESTQuery({
          path: `${apiConstants.ADMIN_USERS_REST_URL}/validate`,
          method: HTTP_METHODS.POST,
          body: requestParams
        })
    }),
    getAdminUserByID: builder.query({
      query: (requestParams: { AdminUserID: number }) =>
        generateRESTQuery({
          path: `${apiConstants.ADMIN_USERS_REST_URL}/${requestParams.AdminUserID}`
        }),
      providesTags: [{ type: 'AdminUserDetails' }],
      async onQueryStarted ({}, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          if (data) {
            dispatch(setAdminUserInfo(data))
          }
        } catch (err: any) {
          handleError(err, dispatch)
        }
      }
    }),
    editAdminUser: builder.mutation({
      query: (body: EditAdminUserFormFields) =>
        generateRESTQuery({
          path: apiConstants.ADMIN_USERS_REST_URL,
          method: HTTP_METHODS.PUT,
          body
        }),
      invalidatesTags: [{ type: 'AdminUserDetails' }]
    }),
    deleteAdminUser: builder.mutation({
      query: (body: DeleteAdminUserParams) =>
        generateRESTQuery({
          path: apiConstants.ADMIN_USERS_REST_URL,
          method: HTTP_METHODS.DELETE,
          body
        }),
      invalidatesTags: ['AllAdminUsers', 'AdminUserDetails']
    }),
    updateAdminUserStatus: builder.mutation({
      query: (body: UpdateAdminUserStatusParams) =>
        generateRESTQuery({
          path: `${apiConstants.ADMIN_USERS_REST_URL}/status`,
          method: HTTP_METHODS.PATCH,
          body
        }),
      invalidatesTags: ['AllAdminUsers', 'AdminUserDetails']
    }),
    updateAdminUserPermissions: builder.mutation({
      query: (requestParams: UpdateAdminUserPermissionsParams) =>
        generateRESTQuery({
          path: `${apiConstants.ADMIN_USERS_REST_URL}/permissions`,
          method: HTTP_METHODS.PUT,
          body: requestParams
        }),
      invalidatesTags: [{ type: 'AdminUserDetails' }]
    }),
    getAdminUserPermissions: builder.mutation({
      query: (requestParams: { AdminUserID: number }) =>
        generateRESTQuery({
          path: `${apiConstants.ADMIN_USERS_REST_URL}/permissions/${requestParams.AdminUserID}`
        }),
      async onQueryStarted ({}, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          if (data) {
            dispatch(setUserPermission(data))
            dispatch(setPermissionsRefetched(true))
          }
        } catch (err: any) {
          handleError(err, dispatch)
        }
      }
    })
  })
})

export const {
  useGetAdminUserByIDQuery,
  useGetAllAdminUsersQuery,
  useValidateAdminUserFormFieldsMutation,
  useCreateNewAdminUserMutation,
  useEditAdminUserMutation,
  useDeleteAdminUserMutation,
  useUpdateAdminUserStatusMutation,
  useUpdateAdminUserPermissionsMutation,
  useGetAdminUserPermissionsMutation
} = adminUsersApi
